import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Flex, Box, Text } from 'rebass/styled-components'
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_HC_PATIENT_CALENDLY_EVENTS, GET_HEALTH_COACH_VISIT_SUMMARIES_ALL, GET_PATIENT_BY_ID } from '../../graphql/queries';
import { ICalendlyEvent, IGetPatientCalendlyEventsInput, IGetPatientCalendlyEventsOutput } from '../../graphql/types/queries/appointment';
import moment from 'moment';
import { Skeleton } from '../../components/loadingSkeleton/Skeleton';
import SwitchSelector from 'react-switch-selector';
import Select, { components } from "react-select";
import { ReactSelectStyles } from '../../styles/ReactSelectStyles';
import { RbCircledChecked, RbCircledUnchecked } from '../../components/RadioButton';
import { useIsVisible } from '../../hooks/useIsVisible';
import { EnhancedModal } from '../../components/EnhancedModal';
import { VisitSummary } from './visitSummary/VisitSummary';
import { IGetProviderPatientByIdInput, IGetProviderPatientByIdOutput } from '../../graphql/types/queries/patient';
import { IGetHealthCoachVisitSummariesInput, IGetHealthCoachVisitSummariesOutput, IHealthCoachVisitSummary } from '../../graphql/types/queries/healthCoaching';

interface IAppointmentsProps {
  id: string
  isMinimized?: boolean
}
const ListContainer = styled(Flex)`
  width: 100%;
  overflow-y: auto;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
  &.selected {
    display: none;
  }
`;

const Option = (props: any) => {
  return (
    <components.Option {...props}>
      <Flex alignItems={'center'}>
        {props.isSelected ? (
          <RbCircledChecked />
        ) : <RbCircledUnchecked />}

        <Text ml={2}>{props.children}</Text>
      </Flex>
    </components.Option>
  );
};

const iconStyle = {
  width: '20px',
  height: '20px',
  background: '#FFFFFF',
  border: '3px solid #BCFFEB',
  margin: '10px',
  boxShadow: 'initial'
}

const iconStyleCancelled = {
  width: '20px',
  height: '20px',
  background: '#FFFFFF',
  border: '3px solid rgba(203, 40, 40, 0.7)',
  margin: '10px',
  boxShadow: 'initial'
}

const contentStyle = {
  background: '#F0F5FD',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
  borderRadius: '10px',
  padding: '12px 0px',
}

const contentStyleMinimized = {
  background: '#F0F5FD',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
  borderRadius: '10px',
  padding: '12px 0px',
  marginBottom: '12px'
}

const contentArrowStyle = {
  borderRight: '7px solid  #F0F5FD',
}

const SelectorLarge = styled(Flex)`
  display: none;
  @media screen and (min-width: 1740px) {
    display: initial;
  }
`

const SelectorSmall = styled(Flex)`
  display: none;
  @media screen and (max-width: 1739px) {
    display: initial;
  }
`

const VisitSummaryButton = styled(Flex)`
  padding: 4px 16px;
  background: #e4e4e4 ;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: #c5f1fa ;
  }
`

const AppointmentDisplay = ({ data, isPast, athenaId, userId, dataSummaries }: { data: ICalendlyEvent, isPast: boolean, athenaId: string, userId?: string, dataSummaries: Array<IHealthCoachVisitSummary> | undefined | null }) => {
  const [checkout, setCheckout] = useState(false)

  return (
    <Flex flexDirection={'column'} width='100%'>
      <Flex flexDirection={['column', 'row', 'row']}>
        <Flex flexDirection={['row', 'column', 'column']} width={['100%', '120px', '120px']} paddingX={['12px', 0, 0]} justifyContent={['flex-start', 'center', 'center']} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={400} fontSize='14px'>{`${moment(data.startTime).format('MM/DD/YYYY')}`}</Text>
            <Text fontWeight={300} fontSize='12px'>{moment(data.startTime).format('hh:mmA z')}-{moment(data.endTime).format('hh:mmA z')}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none', 'initial', 'initial']} />
        <Flex mt={[2, 0, 0]} flexDirection={'column'} width={['100%', '180px', '180px']} paddingX={'12px'} justifyContent={['flex-start', 'center', 'center']} alignItems={['flex-start', 'center', 'center']}>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'>Event Name</Text>
            <Text fontWeight={300} fontSize='14px'>{data.name}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none', 'initial', 'initial']} />
        {data.cancellation ? (
          <Flex mt={[2, 0, 0]} flexDirection={'column'} flex={1} justifyContent={['flex-start', 'center', 'center']} alignItems='flex-start' paddingX={'12px'}>
            <Flex flexDirection={'column'}>
              <Text fontWeight={300} color='rgba(203, 40, 40, 0.7)' fontSize='12px'>Cancelled</Text>
              <Text fontWeight={300} fontSize='14px'>By {data.cancellation.canceledBy} - Reason: {data.cancellation.reason}</Text>
            </Flex>
          </Flex>
        ) : (<Flex flex={1} />)}
        {dataSummaries && dataSummaries.length > 0 && dataSummaries.filter(x => x.calendlyUri === data.uri).length > 0 ? (
          <VisitSummaryButton onClick={() => {
            setCheckout(true)
          }} justifySelf={'flex-end'} alignSelf={'center'} mr={3} alignItems={'center'}>
            <Box mr={2} style={{ background: 'green', height: '6px', width: '6px', borderRadius: '4px' }} />
            <Text fontWeight={300} fontSize='12px'>Checked-out</Text>
          </VisitSummaryButton>
        ) : (
          <>
            {!data.cancellation && (
              <VisitSummaryButton onClick={() => {
                setCheckout(true)
              }} justifySelf={'flex-end'} alignSelf={'center'} mr={3} alignItems={'center'}>
                <Box mr={2} style={{ background: 'orange', height: '6px', width: '6px', borderRadius: '4px' }} />
                <Text fontWeight={300} fontSize='12px'>Start checkout</Text>
              </VisitSummaryButton>
            )}
          </>
        )}
      </Flex>
      
      {!!checkout && !!userId && (
        <EnhancedModal
          containerStyle={{ paddingRight: 0, marginRight: 0 }}
          isOpen={!!checkout}
          onClose={() => setCheckout(false)}>
          <VisitSummary calendlyUri={data.uri} athenaId={athenaId} userId={userId} onClose={() => setCheckout(false)} />
        </EnhancedModal>
      )}
    </Flex>
  )
}

const AppointmentDisplayMinimized = ({ data, isPast, athenaId, userId, dataSummaries }: { data: ICalendlyEvent, isPast: boolean, athenaId: string, userId?: string, dataSummaries: Array<IHealthCoachVisitSummary> | undefined | null }) => {
  const [checkout, setCheckout] = useState(false)

  return (
    <Flex flexDirection={'column'} width='100%'>
      <Flex flexDirection={['column']}>
        <Flex flexDirection={['row']} width={['100%']} paddingX={['12px']} justifyContent={['flex-start']} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={400} fontSize='14px'>{`${moment(data.startTime).format('MM/DD/YYYY')}`}</Text>
            <Text fontWeight={300} fontSize='12px'>{moment(data.startTime).format('hh:mmA z')}-{moment(data.endTime).format('hh:mmA z')}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none']} />
        <Flex mt={[2]} flexDirection={'column'} width={['100%']} paddingX={['12px']} justifyContent={['flex-start']} alignItems={['flex-start']}>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'>Event Name</Text>
            <Text fontWeight={300} fontSize='14px'>{data.name}</Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} display={['none']} />
        {data.cancellation && (
          <Flex mt={[2, 0, 0]} flexDirection={'column'} flex={1} justifyContent={['flex-start', 'center', 'center']} alignItems='flex-start' paddingX={'12px'}>
            <Flex flexDirection={'column'}>
              <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'>Cancelled</Text>
              <Text fontWeight={300} fontSize='14px'>By {data.cancellation.canceledBy} - Reason: {data.cancellation.reason}</Text>
            </Flex>
          </Flex>
        )}
        {dataSummaries && dataSummaries.length > 0 && dataSummaries.filter(x => x.calendlyUri === data.uri).length > 0 ? (
          <VisitSummaryButton mt={3} onClick={() => {
            setCheckout(true)
          }} justifySelf={'flex-end'} alignSelf={'center'} mr={3} alignItems={'center'}>
            <Box mr={2} style={{ background: 'green', height: '6px', width: '6px', borderRadius: '4px' }} />
            <Text fontWeight={300} fontSize='12px'>Checked-out</Text>
          </VisitSummaryButton>
        ) : (
          <>
            {!data.cancellation && (
              <VisitSummaryButton mt={3} onClick={() => {
                setCheckout(true)
              }} justifySelf={'flex-end'} alignSelf={'center'} mr={3} alignItems={'center'}>
                <Box mr={2} style={{ background: 'orange', height: '6px', width: '6px', borderRadius: '4px' }} />
                <Text fontWeight={300} fontSize='12px'>Start checkout</Text>
              </VisitSummaryButton>
            )}
          </>
        )}
      </Flex>
      {!!checkout && !!userId && (
        <EnhancedModal
          containerStyle={{ paddingRight: 0, marginRight: 0 }}
          isOpen={!!checkout}
          onClose={() => setCheckout(false)}>
          <VisitSummary calendlyUri={data.uri} athenaId={athenaId} userId={userId} onClose={() => setCheckout(false)} />
        </EnhancedModal>
      )}
    </Flex>
  )
}

const AppointmentDisplayLoading = ({ isPast }: { isPast: boolean }) => {
  return (
    <Flex flexDirection={'column'} width='100%'>
      <Flex flexDirection={['column', 'row', 'row']}>
        <Flex flexDirection={'column'} width='120px' justifyContent={'center'} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={400} fontSize='14px'><Skeleton height={19} width={90} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
            <Text fontWeight={300} fontSize='12px'><Skeleton height={16} width={100} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} />
        <Flex flexDirection={'column'} width='180px' justifyContent={'center'} alignItems='center'>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'><Skeleton height={16} width={112} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
            <Text fontWeight={300} fontSize='14px'><Skeleton height={19} width={112} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
          </Flex>
        </Flex>
        <Box style={{ height: '48px', background: '#E2E2E2', width: '1px' }} />
        <Flex flexDirection={'column'} flex={1} justifyContent={'center'} alignItems='flex-start' paddingX={'12px'}>
          <Flex flexDirection={'column'}>
            <Text fontWeight={300} color='rgba(52, 79, 122, 0.6)' fontSize='12px'><Skeleton height={16} width={75} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
            <Text fontWeight={300} fontSize='14px'><Skeleton height={19} width={163} style={{ marginTop: '1px', marginBottom: '1px' }} /></Text>
          </Flex>
        </Flex>
        {!!isPast && (
          <Skeleton height={18} width={61} style={{ marginRight: '12px' }} />
        )}
      </Flex>
    </Flex>
  )
}

const options = [
  {
    label: 'Upcoming',
    value: 'upcoming',
    selectedBackgroundColor: "#BCFFEB",
  },
  {
    label: "Past",
    value: "past",
    selectedBackgroundColor: "#BCFFEB"
  },
];

const optionsMobile = [
  {
    label: 'Upcoming',
    value: 'upcoming',
    selectedBackgroundColor: "#BCFFEB",
  },
  {
    label: "Past",
    value: "past",
    selectedBackgroundColor: "#BCFFEB"
  },
];

export const Calendly: React.FC<IAppointmentsProps> = ({ id, isMinimized }) => {
  const [loadSummaries, { data: dataSummaries, loading: loadingSummaries }] = useLazyQuery<IGetHealthCoachVisitSummariesOutput, IGetHealthCoachVisitSummariesInput>(GET_HEALTH_COACH_VISIT_SUMMARIES_ALL)
  const { data: dataPatient, loading: loadingPatient, error: errorPatient } = useQuery<IGetProviderPatientByIdOutput, IGetProviderPatientByIdInput>(GET_PATIENT_BY_ID, {
    variables: {
      id
    },
    onCompleted: e => {
      if (e.getProviderPatientById && e.getProviderPatientById.user) {
        loadSummaries({
          variables: {
            userId: e.getProviderPatientById.user
          }
        })
      }
    }
  })

  const [selectedPeriod, setselectedPeriod] = useState<'upcoming' | 'past'>('upcoming');
  const [appointmentData, setAppointmentData] = useState<Array<ICalendlyEvent> | null>(null);
  const [isParsingData, setIsParsingData] = useState<boolean>(false);

  const ref = useRef<any>();
  const isVisible = useIsVisible(ref);

  const [load, { data, loading, error }] = useLazyQuery<IGetPatientCalendlyEventsOutput, IGetPatientCalendlyEventsInput>(GET_HC_PATIENT_CALENDLY_EVENTS, {
    variables: {
      id: id
    }
  })

  useEffect(() => {
    if (!!isVisible) {
      load()
    }
  }, [isVisible])

  useEffect(() => {
    if (!!loading || !selectedPeriod || !data?.healthCoachGetCalendlyEventsForPatient) {
      setAppointmentData(null)
      return
    }

    setIsParsingData(true)

    let res: Array<ICalendlyEvent> = []

    for (let index = 0; index < data.healthCoachGetCalendlyEventsForPatient.events.length; index++) {
      const element = data.healthCoachGetCalendlyEventsForPatient.events[index];
      const fullDate = moment(`${element.startTime}`)
      let isFutureDate = moment().isBefore(fullDate)
      if (!!isFutureDate && selectedPeriod === 'upcoming') {
        res.push({
          ...element,
        })
      } else if (!isFutureDate && selectedPeriod === 'past') {
        res.push({
          ...element,
        })
      }
    }

    let ordered = res.sort((a, b) => {
      if (moment(a.startTime).isBefore(b.startTime)) return 1
      return -1
    })

    setAppointmentData(ordered)
    setIsParsingData(false)

    return () => {

    }
  }, [data, loading, selectedPeriod]);

  return (
    <Flex ref={ref} flexDirection={'column'} width='100%' px={isMinimized ? '0' : '20px'} paddingY={'20px'}>
      <Flex flexDirection={'column'} style={{ width: '100%', flex: 'initial', overflow: 'hidden' }} paddingY={'12px'} mb={0}>
        <Flex flexDirection={['column', 'row', 'row']} alignItems={!!isMinimized ? 'baseline' : ''}>
          <Text fontWeight={600} fontSize='18px' m={'18px 28px'}>Calendly Schedules</Text>
          <Flex flex={1} mt={[2, 0, 0]} justifyContent='flex-start' alignItems={'center'}>
            <Flex width={['100%']} minWidth={isMinimized ? '0' : '620px'} paddingX={['12px', 0, 0]} height={['30px', '30px', '30px']}>
              <>
                {!isMinimized ? (
                  <>
                    <SelectorLarge flex={1} minWidth={'min(640px, 100%)'} maxWidth={'min(640px, 100%)'}>
                      <SwitchSelector
                        onChange={(val) => {
                          setselectedPeriod(val as 'upcoming' | 'past')
                        }}
                        forcedSelectedIndex={['upcoming', 'past'].indexOf(selectedPeriod)}
                        options={options}
                        backgroundColor={"transparent"}
                        fontColor={"#344F7A"}
                        selectedFontColor={"#344F7A"}
                        selectedBackgroundColor={"#BCFFEB"}
                      />
                    </SelectorLarge>
                    <SelectorSmall flex={1} maxWidth={'200px'}>
                      <Select
                        isSearchable={false}
                        styles={ReactSelectStyles.initialStyles}
                        options={optionsMobile}
                        value={optionsMobile[optionsMobile.findIndex(x => x.value === selectedPeriod)]}
                        onChange={(val) => {
                          setselectedPeriod(val && val.value ? val.value as any : 'past')
                        }}
                        components={{ Option }} />
                    </SelectorSmall>
                  </>
                ) : (<Select
                  styles={ReactSelectStyles.initialStyles}
                  isSearchable={false}
                  options={options as any}
                  value={options.find(x => x.value === selectedPeriod)}
                  onChange={(option: any) => {
                    if (!option) return
                    setselectedPeriod(option.value as 'upcoming' | 'past')
                  }} />)}
              </>
            </Flex>
          </Flex>
        </Flex>
        
        <Flex style={{ overflowY: 'hidden' }}>
          {!!loading || !!isParsingData || !!loadingPatient ? (
            <ListContainer>
              <VerticalTimeline layout='1-column' lineColor='#344F7A' >
                {[0, 1, 2, 3].map(x => {
                  return (
                    <VerticalTimelineElement
                      className="vertical-timeline-element--work"
                      contentStyle={contentStyle}
                      contentArrowStyle={contentArrowStyle}
                      iconStyle={iconStyle} key={`loadingCalendlyAppts${x}`}
                      style={{
                        margin: 0,
                        marginBottom: '12px',
                      }}
                    >
                      <AppointmentDisplayLoading isPast={selectedPeriod === 'past'} />
                    </VerticalTimelineElement>)
                })}
              </VerticalTimeline>
            </ListContainer>
          ) : (
            <>
              <ListContainer>
                {appointmentData && appointmentData.length > 0 ? (
                  <>
                    <VerticalTimeline layout='1-column' lineColor='#344F7A' >
                      {(appointmentData).map(x => {
                        return (
                          <VerticalTimelineElement
                            className="vertical-timeline-element--work"
                            contentStyle={!isMinimized ? contentStyle : contentStyleMinimized}
                            contentArrowStyle={contentArrowStyle}
                            style={{
                              margin: 0,
                              marginBottom: '12px',
                              opacity: !!x.cancellation ? 0.7 : 1
                            }}
                            iconStyle={!!x.cancellation ? iconStyleCancelled : iconStyle} key={x.uri}
                          >
                            {!isMinimized ? <AppointmentDisplay dataSummaries={dataSummaries?.healthCoachGetPatientVisitSummaries} userId={(dataPatient?.getProviderPatientById || {} as any).user} athenaId={id} isPast={selectedPeriod === 'past'} data={x} /> : <AppointmentDisplayMinimized dataSummaries={dataSummaries?.healthCoachGetPatientVisitSummaries} userId={(dataPatient?.getProviderPatientById || {} as any).user} athenaId={id} isPast={selectedPeriod === 'past'} data={x} />}
                          </VerticalTimelineElement>)
                      })}
                    </VerticalTimeline>
                  </>
                ) : (
                  <Flex m={4} justifyContent='center' alignItems={'center'} width='100%'>
                    <Text textAlign='center'>No Calendly Events For The Selected Period</Text>
                  </Flex>
                )}
              </ListContainer>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>);
}